import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, formatMoney, hoy, newId, parseMoney, type Centavos, type CuentaBancoCordobas } from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo } from './Campo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { Modal } from './Modal.js';
import { SaleDeLaGaveta, faltaEnGaveta, useGaveta } from './SaleDeLaGaveta.js';

type FormaDelAbono = 'efectivo_cordobas' | 'transferencia' | 'billetera';

/** The client's «Abonar» buttons (`AbonoModal`), here for money going the other way. */
const FORMAS: { forma: FormaDelAbono; etiqueta: Clave; icono: NombreIcono }[] = [
  { forma: 'efectivo_cordobas', etiqueta: 'pago.efectivo_cordobas', icono: 'caja' },
  { forma: 'transferencia', etiqueta: 'pago.transferencia', icono: 'transferencias' },
  { forma: 'billetera', etiqueta: 'pago.billetera', icono: 'billetera' },
];

/** What is being paid off: a purchase on credit, or an expense bought on credit, and what is still owed on it. */
export interface DeudaPorAbonar {
  tipo: 'compra' | 'gasto';
  id: string;
  saldo: Centavos;
}

/**
 * «Abonar» on «Cuentas por pagar»: some of what is owed to a supplier, or on an expense bought on
 * credit, paid now, and how.
 *
 * It used to ask for the amount alone and record every payment as cash in córdobas, so a supplier
 * paid by transfer came out of «Efectivo general» in the books and the bank was left too high (the
 * audit of 2026-10-17). It used to close before the books had looked at it, too, so a payment they
 * refused, more than was owed, say, went without a word. Now it asks how it was paid, as the
 * client's «Abonar» does and as «Cuadrar la semana» asks for the same payment (`PasosDePago`): in
 * cash, out of general cash, or out of the drawer with «Sale de la gaveta», off until somebody
 * turns it on (2026-10-17); by transfer, out of which account when the shop has more than one; or
 * from the mobile wallet. A transfer's or a wallet's number goes with it if it is typed. More than
 * is owed is said under the amount, and a refusal under the buttons, and the sheet stays open.
 */
export function AbonarDeuda({ deuda, onCerrar }: { deuda: DeudaPorAbonar; onCerrar: () => void }) {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [texto, setTexto] = useState('');
  const [forma, setForma] = useState<FormaDelAbono>('efectivo_cordobas');
  /** The córdoba account a transfer went out of, once picked: the one transfers go to until then. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBancoCordobas | null>(null);
  const cuenta = cuentaEnCordobas(config, cuentaElegida);
  const [referencia, setReferencia] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const gaveta = useGaveta(forma, { encendidaAlEmpezar: false });

  const monto = parseMoney(texto);
  const valido = monto !== null && monto > 0 && deuda.saldo >= monto;

  async function abonar() {
    if (monto === null || !valido || guardando) return;
    const problema = faltaEnGaveta(gaveta, monto, t);
    setError(problema);
    if (problema) return;
    setGuardando(true);
    try {
      const pago = {
        fecha: hoy(),
        monto,
        formaPago: forma,
        ...(gaveta.sesionId ? { sesionId: gaveta.sesionId } : {}),
        ...(forma === 'transferencia' ? { cuenta } : {}),
        ...(forma !== 'efectivo_cordobas' && referencia.trim() ? { referencia: referencia.trim() } : {}),
        // Named, so it can be voided if it was never made.
        pagoId: newId(),
      };
      const { rechazo } =
        deuda.tipo === 'compra'
          ? await emitirYVer('compra.pagada', { compraId: deuda.id, ...pago })
          : await emitirYVer('gasto.pagado', { gastoId: deuda.id, ...pago });
      if (rechazo) {
        setError(rechazo);
        setGuardando(false);
        return;
      }
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
      setGuardando(false);
    }
  }

  return (
    <Modal
      titulo={t(deuda.tipo === 'compra' ? 'preguntar.abonar' : 'preguntar.abonarGasto')}
      onCerrar={onCerrar}
      pie={
        <div className="flex gap-3">
          <button type="button" className="btn-secundario flex-1" onClick={onCerrar}>
            <Ico icono="cancelar" />
            {t('comun.cancelar')}
          </button>
          <button type="button" className="btn-primario flex-1" disabled={!valido || guardando} onClick={() => void abonar()}>
            <Ico icono="pagar" />
            {t('comun.abonar')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-abonar-deuda={deuda.id}>
        <Campo
          etiqueta={t('preguntar.cuanto')}
          ayuda={t('preguntar.faltaPagar', { monto: formatMoney(deuda.saldo) })}
          placeholder="0.00"
          inputMode="decimal"
          className="tabular text-lg"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void abonar()}
          autoFocus
          autoComplete="off"
          error={texto.trim() !== '' && !valido ? t('pagoPaso.montoInvalido') : null}
        />

        <div>
          <span className="etiqueta">{t('gastos.comoSePago')}</span>
          <div className="grid grid-cols-3 gap-2">
            {FORMAS.map((f) => (
              <button
                key={f.forma}
                type="button"
                onClick={() => setForma(f.forma)}
                aria-pressed={forma === f.forma}
                data-abonar-forma={f.forma}
                className={`flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-xs font-medium transition ${
                  forma === f.forma ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                <Ico icono={f.icono} className="size-5" />
                <span className="text-center leading-tight">{t(f.etiqueta)}</span>
              </button>
            ))}
          </div>
          {/* Where it comes out of. In cash with a till open here, the switch under the buttons says. */}
          {!(forma === 'efectivo_cordobas' && gaveta.aplica) && (
            <p className="mt-2 text-xs text-slate-400" data-abonar-de-donde>
              {t(
                forma === 'efectivo_cordobas'
                  ? 'pagoPaso.efectivoDetalle'
                  : forma === 'transferencia'
                    ? 'gastoPaso.transferenciaDetalle'
                    : 'gastoPaso.billeteraDetalle',
              )}
            </p>
          )}
        </div>

        <SaleDeLaGaveta gaveta={gaveta} />
        {forma === 'transferencia' && (
          <ElegirCuenta valor={cuenta} onCambiar={(c) => setCuentaElegida(c as CuentaBancoCordobas)} etiqueta={t('bancos.pagaDe')} />
        )}
        {forma !== 'efectivo_cordobas' && (
          <Campo
            etiqueta={t('comun.referencia')}
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            autoComplete="off"
            maxLength={80}
          />
        )}
        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}
